<template>
  <v-dialog v-model="dialog" scrollable max-width="920px">
    <v-card>

      <!-- ── HEADER ───────────────────────────────────────── -->
      <v-card-title class="d-flex align-center py-4">
        <div>
          <span class="text-h6">{{ company.name }}</span>
          <span class="text--secondary ml-2 body-2">#{{ company.id }}</span>
        </div>
        <v-spacer />
        <status-chip :value="company.status" class="mr-2" />
        <v-chip v-if="company.is_beta_tester" color="info" small>
          <v-icon left small>mdi-bug</v-icon>Beta Tester
        </v-chip>
      </v-card-title>

      <v-card-subtitle class="pb-0">
        Registrado em {{ $format.dateBr(company.created_at) }}
        <span v-if="company.visited_at" class="ml-3 text--secondary">
          · Último acesso {{ $format.dateBr(company.visited_at) }}
        </span>
      </v-card-subtitle>

      <v-divider class="mt-3" />

      <v-card-text>

        <!-- ── INFORMAÇÕES BÁSICAS ───────────────────────── -->
        <div class="section-title mt-4">

          <div class="d-flex   justify-space-between">

            <div class='mr-4'>
              <v-icon left color="primary">mdi-account-circle</v-icon>
              Responsável
            </div>

            <div>
              <v-btn x-small outlined color="primary" @click="copyToClipboard()">
                <v-icon x-small>mdi-content-copy</v-icon>
                Copiar dados
              </v-btn>
            </div>



          </div>


        </div>
        <v-row dense class="mt-1">
          <v-col cols="12" sm="6">
            <span class="text--secondary caption">Nome</span>
            <div>{{ company.owner.user.name }}</div>
          </v-col>
          <v-col cols="12" sm="6">
            <span class="text--secondary caption">E-mail</span>
            <div>{{ company.owner.user.email }}</div>
          </v-col>
          <v-col cols="12" sm="6">
            <span class="text--secondary caption">WhatsApp</span>
            <div>{{ company.owner.user.phone_wa }}</div>
          </v-col>
          <v-col cols="12" sm="6">
            <span class="text--secondary caption">Modo</span>
            <div>{{ company.type }}</div>
          </v-col>
        </v-row>

        <!-- ── ENDEREÇO ──────────────────────────────────── -->
        <div class="section-title mt-5">
          <v-icon left small color="primary">mdi-map-marker</v-icon>
          Endereço
        </div>
        <v-row dense class="mt-1">
          <v-col cols="12" sm="4">
            <span class="text--secondary caption">CEP</span>
            <div>{{ $format.cep(company.owner.user.main_address.postal_code) }}</div>
          </v-col>
          <v-col cols="12" sm="8">
            <span class="text--secondary caption">Logradouro</span>
            <div>
              {{ company.owner.user.main_address.address }},
              {{ company.owner.user.main_address.number }}
            </div>
          </v-col>
          <v-col cols="12" sm="4">
            <span class="text--secondary caption">Bairro</span>
            <div>{{ company.owner.user.main_address.district }}</div>
          </v-col>
          <v-col cols="12" sm="4">
            <span class="text--secondary caption">Cidade</span>
            <div>{{ company.owner.user.main_address.city }}</div>
          </v-col>
          <v-col cols="12" sm="4">
            <span class="text--secondary caption">Estado</span>
            <div>{{ company.owner.user.main_address.state }}</div>
          </v-col>
        </v-row>

        <!-- ── DADOS DE COBRANÇA ─────────────────────────── -->
        <template v-if="billingInformation">
          <v-divider class="mt-4" />
          <div class="section-title mt-4">
            <v-icon left small color="primary">mdi-credit-card-outline</v-icon>
            Dados de Cobrança
          </div>
          <v-row dense class="mt-1">
            <v-col cols="12" sm="6">
              <span class="text--secondary caption">Nome</span>
              <div>{{ billingInformation.name }}</div>
            </v-col>
            <template v-if="billingInformation.person_type !== 'br_cpf'">
              <v-col cols="12" sm="6">
                <span class="text--secondary caption">Nome Fantasia</span>
                <div>{{ billingInformation.alias }}</div>
              </v-col>
              <v-col cols="12" sm="6">
                <span class="text--secondary caption">CNPJ</span>
                <div>{{ billingInformation.cnpj }}</div>
              </v-col>
              <v-col cols="12" sm="6">
                <span class="text--secondary caption">Responsável</span>
                <div>{{ billingInformation.responsible_name }}</div>
              </v-col>
            </template>
            <v-col v-if="billingInformation.person_type === 'br_cpf'" cols="12" sm="6">
              <span class="text--secondary caption">CPF</span>
              <div>{{ billingInformation.cpf }}</div>
            </v-col>
            <v-col cols="12" sm="6">
              <span class="text--secondary caption">E-mail</span>
              <div>{{ billingInformation.email }}</div>
            </v-col>
            <v-col cols="12" sm="6">
              <span class="text--secondary caption">Telefone</span>
              <div>{{ billingInformation.phone }}</div>
            </v-col>
          </v-row>
        </template>

        <!-- ── PLANO / ASSINATURA ────────────────────────── -->
        <v-divider class="mt-4" />
        <div class="section-title mt-4 d-flex align-center">
          <v-icon left small color="primary">mdi-certificate</v-icon>
          Plano
          <v-btn icon small text color="primary" class="ml-auto" @click="openUpdateCompanyForm()">
            <v-icon small>mdi-cog</v-icon>
          </v-btn>
        </div>
        <v-row dense class="mt-1">
          <v-col cols="12" sm="6">
            <span class="text--secondary caption">Status</span>
            <div>
              <span v-if="company.is_free_account == 1">Acesso Gratuito</span>
              <span v-else-if="company.last_subscription">{{ company.last_subscription.plan.name }}</span>
              <span v-else>Período de Teste até {{ $format.dateBr(company.free_trial_ends_at) }}</span>
            </div>
          </v-col>
          <v-col v-if="company.no_block_access_before" cols="12" sm="6">
            <span class="text--secondary caption">Não bloquear antes de</span>
            <div>{{ $format.dateBr(company.no_block_access_before) }}</div>
          </v-col>
          <v-col cols="12" sm="6">
            <span class="text--secondary caption">Limite de profissionais</span>
            <div>{{ company.professionals_limit }}</div>
          </v-col>
        </v-row>
        <v-btn small text color="success" class="mt-2 px-0" @click="viewUserSubscriptions(company.id)">
          <v-icon left small>mdi-eye</v-icon>
          Ver assinaturas
        </v-btn>

        <!-- ── WHATSAPP ───────────────────────────────────── -->
        <v-divider class="mt-4" />
        <div class="section-title mt-4">
          <v-icon left small color="primary">mdi-whatsapp</v-icon>
          WhatsApp
        </div>

        <v-row dense class="mt-2">
          <v-col cols="12" sm="auto">
            <v-btn small text color="primary" @click="openWhatsappNotificationForm()">
              <v-icon left small>mdi-bell-ring-outline</v-icon>
              Configurar Z-API
            </v-btn>
          </v-col>
          <v-col cols="12" sm="auto">
            <v-btn small text color="primary" @click="resendSchedule()">
              <v-icon left small>mdi-refresh</v-icon>
              Reenviar Agenda
            </v-btn>
          </v-col>
          <v-col cols="12" sm="auto">
            <v-btn small text color="primary" @click="resendRemindersSession()">
              <v-icon left small>mdi-refresh</v-icon>
              Reenviar Lembretes
            </v-btn>
          </v-col>
        </v-row>

        <template v-if="company.billing_whats_app_instance_id">
          <div class="text--secondary caption mt-3 mb-1">Instância de Cobrança</div>
          <v-chip outlined small class="mr-2">{{ billingWhatsAppInstance.name }}</v-chip>
          <v-chip outlined small class="mr-2">{{ billingWhatsAppInstance.number }}</v-chip>
          <v-chip outlined small>ID: {{ billingWhatsAppInstance.instance_id }}</v-chip>
        </template>

        <template v-if="company.session_whats_app_instance_id">
          <div class="text--secondary caption mt-3 mb-1">Instância de Sessão</div>
          <v-chip outlined small class="mr-2">{{ sessionWhatsAppInstance.name }}</v-chip>
          <v-chip outlined small class="mr-2">{{ sessionWhatsAppInstance.number }}</v-chip>
          <v-chip outlined small>ID: {{ sessionWhatsAppInstance.instance_id }}</v-chip>
        </template>

        <!-- ── EXPORTAR DADOS ────────────────────────────── -->
        <v-divider class="mt-4" />
        <div class="section-title mt-4">
          <div class="d-flex flex-column">
            <span>
              <v-icon left small color="primary">mdi-database-export</v-icon>
              Exportar Dados
            </span>
            <small>
              <strong>*Uso exclusivo - Welves*</strong>
            </small>

          </div>
        </div>
        <v-row dense class="mt-2">
          <v-col cols="auto" v-for="entity in exportEntities" :key="entity.value">
            <v-btn small outlined color="primary" @click="exportRaw(entity.value)">
              {{ entity.label }}
            </v-btn>
          </v-col>
        </v-row>

        <!-- ── FERRAMENTAS ───────────────────────────────── -->
        <v-divider class="mt-4" />
        <div class="section-title mt-4">
          <v-icon left small color="primary">mdi-wrench</v-icon>
          Ferramentas
        </div>
        <v-row dense class="mt-2">
          <v-col cols="auto">
            <v-btn small color="deep-purple" dark @click="openCloneDialog()">
              <v-icon left small>mdi-content-copy</v-icon>
              Clonar Dados
            </v-btn>
          </v-col>
        </v-row>

        <!-- ── OBSERVAÇÕES ───────────────────────────────── -->
        <v-divider class="mt-4" />
        <div class="section-title mt-4">
          <v-icon left small color="primary">mdi-note-text</v-icon>
          Observações
        </div>
        <v-row dense class="mt-2">
          <v-col cols="12">
            <v-textarea outlined v-model="company.observations" rows="2" auto-grow hide-details />
          </v-col>
          <v-col cols="12" class="d-flex justify-end">
            <v-btn small color="success" @click="storeObservations()">Salvar</v-btn>
          </v-col>
        </v-row>

        <!-- ── METADATA ──────────────────────────────────── -->
        <v-divider class="mt-4" />
        <div class="section-title mt-4">
          <v-icon left small color="primary">mdi-code-braces</v-icon>
          Metadata
        </div>
        <div class="mt-1">
          <small class="text--secondary">{{ company.research }}</small><br />
          <small class="text--secondary">{{ company.signup_metadata }}</small>
        </div>

        <!-- ── USUÁRIOS ──────────────────────────────────── -->
        <v-divider class="mt-4" />
        <div class="section-title mt-4">
          <v-icon left small color="primary">mdi-account-group</v-icon>
          Usuários
        </div>
        <v-data-table :headers="userHeaders" :items="company.users" disable-sort dense hide-default-footer
          :items-per-page="-1" class="mt-2">
          <template v-slot:[`item.type`]="{ item }">
            {{ getUserType(item) }}
          </template>
          <template v-slot:[`item.actions`]="{ item }">
            <v-menu offset-y>
              <template v-slot:activator="{ on, attrs }">
                <v-btn text icon color="primary" v-bind="attrs" v-on="on">
                  <v-icon>mdi-dots-vertical</v-icon>
                </v-btn>
              </template>
              <v-list dense>
                <v-list-item @click="userLogout(item.id)">
                  <v-list-item-icon><v-icon>mdi-logout</v-icon></v-list-item-icon>
                  <v-list-item-title>Desconectar</v-list-item-title>
                </v-list-item>
                <v-list-item @click="openIaForm(item.id)">
                  <v-list-item-icon><v-icon>mdi-brain</v-icon></v-list-item-icon>
                  <v-list-item-title>Créditos IA</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </template>
        </v-data-table>

        <small v-if="company.recommender_id" class="text--secondary mt-2 d-block">
          Indicado por {{ company.recommendation_by.name }}
        </small>

      </v-card-text>

      <v-divider />
      <v-card-actions class="px-4">
        <v-btn text @click="dialog = false">Fechar</v-btn>
      </v-card-actions>
    </v-card>

    <!-- ── DIALOGS ───────────────────────────────────────── -->
    <UpdateCompanyForm @store="select($event), $emit('store')" ref="UpdateCompanyForm" />
    <CompanySubscriptionForm @store="select($event), $emit('store')" ref="CompanySubscriptionForm" :company="company" />
    <CustomSubscriptionForm @store="select($event), $emit('store')" ref="CustomSubscriptionForm" />
    <WhatsappNotificationForm @store="select($event), $emit('store')" ref="WhatsappNotificationForm" />
    <CompanySubscriptionsView @update="select(company.id)" ref="CompanySubscriptionsView" />
    <UserCreditsIaForm @update="select(company.id)" ref="UserCreditsIaForm" />
    <CloneClinicDialog ref="CloneClinicDialog" />
  </v-dialog>
</template>

<script>
import CompanySubscriptionForm from "@/components/subscription/forms/CompanySubscriptionForm.vue";
import CustomSubscriptionForm from "@/components/subscription/forms/CustomSubscriptionForm.vue";
import UpdateCompanyForm from "@/components/admin/forms/UpdateCompanyForm.vue";
import WhatsappNotificationForm from "@/components/admin/forms/WhatsappNotificationForm.vue";
import CompanySubscriptionsView from "@/components/subscription/sections/CompanySubscriptionsView.vue";
import UserCreditsIaForm from "@/components/admin/forms/UserCreditsIaForm.vue";
import CloneClinicDialog from "@/components/admin/forms/CloneClinicDialog.vue";

export default {
  components: {
    CompanySubscriptionForm,
    CustomSubscriptionForm,
    UpdateCompanyForm,
    UserCreditsIaForm,
    WhatsappNotificationForm,
    CompanySubscriptionsView,
    CloneClinicDialog,
  },

  data() {
    return {
      dialog: false,

      exportEntities: [
        { label: 'Pacientes', value: 'customers' },
        { label: 'Prontuário', value: 'records' },
        { label: 'Anamnese', value: 'anamneses' },
        { label: 'Sessões', value: 'sessions' },
        { label: 'Financeiro', value: 'transactions' },
        { label: 'Documentos', value: 'documents' },
      ],

      userHeaders: [
        { text: 'Nome', value: 'name' },
        { text: 'E-mail', value: 'email' },
        { text: 'Tipo', value: 'type' },
        { text: 'Status', value: 'status' },
        { text: '', value: 'actions', width: '1%' },
      ],

      company: {
        billing_whats_app_instance: {},
        session_whats_app_instance: {},
        billing_information: { main_address: {} },
        subscriptions: [],
        status: 'active',
        owner: { user: { main_address: {} } },
      },
    }
  },

  computed: {
    billingWhatsAppInstance() { return this.company?.billing_whats_app_instance },
    sessionWhatsAppInstance() { return this.company?.session_whats_app_instance },
    billingInformation() { return this.company?.billing_information },
  },

  methods: {
    open(company_id) {
      this.select(company_id)
      this.dialog = true
    },

    select(company_id) {
      this.$loading.start()
      this.$http
        .show('admin/companies', company_id)
        .then(response => {
          this.company = response.company
          this.$loading.finish()
        })
        .catch(() => this.$loading.finish())
    },

    openUpdateCompanyForm() { this.$refs.UpdateCompanyForm.open(this.company.id) },
    openCustomSubscriptionForm() { this.$refs.CustomSubscriptionForm.open(this.company.id) },
    openWhatsappNotificationForm() { this.$refs.WhatsappNotificationForm.open(this.company.id) },
    viewUserSubscriptions(id) { this.$refs.CompanySubscriptionsView.open(id) },
    openIaForm(user_id) { this.$refs.UserCreditsIaForm.open(user_id) },
    openCloneDialog() { this.$refs.CloneClinicDialog.open(this.company) },

    getUserType(user) {
      if (user.company_user?.is_attendant) return 'Atendente'
      if (user.company_user?.is_professional) return 'Profissional'
      if (user.company_user?.is_secretary) return 'Secretária'
      return '—'
    },

    storeObservations() {
      this.$loading.start()
      this.$http
        .update('admin/companies', this.company.id, { observations: this.company.observations })
        .then(() => this.$loading.finish())
        .catch(() => this.$loading.finish())
    },

    resendSchedule() {
      this.$loading.start()
      this.$http
        .show('admin/resend-schedule', this.company.id)
        .then(() => this.$loading.finish())
        .catch(() => this.$loading.finish())
    },

    resendRemindersSession() {
      this.$loading.start()
      this.$http
        .show('admin/resend-reminders-session', this.company.id)
        .then(() => this.$loading.finish())
        .catch(() => this.$loading.finish())
    },

    copyToClipboard() {


      let text = `ID: ${this.company.id}\n`
      text += `Nome: ${this.company.name}\n`
      text += `E-mail: ${this.company.owner.user.email}\n`
      text += `WhatsApp: ${this.company.owner.user.phone_wa}\n`
      text += `Registrado em: ${this.$format.dateBr(this.company.created_at)}\n`
      text += `Último acesso: ${this.company.visited_at ? this.$format.dateBr(this.company.visited_at) : '—'}\n`
      text += `Plano: ${this.company.is_free_account == 1 ? 'Acesso Gratuito' : this.company.last_subscription ? this.company.last_subscription.plan.name : `Período de Teste até ${this.$format.dateBr(this.company.free_trial_ends_at)}`}\n`

      navigator.clipboard.writeText(text)
      this.$snackbar({ message: 'Copiado!', color: 'success', timeout: 1500 })
    },

    userLogout(user_id) {
      this.$http
        .destroy('admin/users', user_id)
        .catch(() => { })
    },

    exportRaw(entity) {
      this.$loading.start()
      this.$http
        .store(`admin/export-raw-job/${entity}`, { company_id: this.company.id })
        .then(response => {
          this.$loading.finish()
          this.$snackbar({ message: response.message, color: 'success', timeout: 4000, showClose: true })
        })
        .catch(() => this.$loading.finish())
    },
  },
}
</script>

<style scoped>
.section-title {
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
}
</style>
